/**
 * Auditoría: quién hizo qué y cuándo (lecturas de datos personales incluidas).
 * Filtrable por acción; los operadores y las personas afectadas llevan a su ficha.
 */
import { useState } from 'react'
import { Link } from 'react-router'
import { useApp } from '../../components/AppContext'
import { ErrorBox, Skeleton } from '../../components/ui'
import { ACCION_LABEL, accionLabel, fechaHora } from '../../lib/adminFormat'
import { useAsync, useDocumentTitle } from '../../lib/hooks'
import { Paginador } from './common'

const LIMITE = 50
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
/** Acciones cuyo `objeto` es una persona con ficha (borrar la cuenta no: ya no existe). */
const SOBRE_PERSONA = new Set(['admin_view_user', 'admin_suspend', 'admin_unsuspend', 'admin_note'])

/** Detalle en una línea legible (sin volcar JSON anidado). */
function resumenDetalle(detalle: unknown): string {
  if (!detalle || typeof detalle !== 'object') return ''
  return Object.entries(detalle as Record<string, unknown>)
    .filter(([, v]) => v !== null && v !== undefined && typeof v !== 'object')
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(' · ')
}

export function Auditoria() {
  useDocumentTitle('Auditoría · Administración')
  const { api } = useApp()
  const [accion, setAccion] = useState('')
  const [desde, setDesde] = useState(0)
  const a = useAsync(
    () =>
      api.adminAuditoria({ accion: accion || undefined, limite: LIMITE, desplazamiento: desde }),
    [api, accion, desde]
  )

  return (
    <div className="stack">
      <div className="admin-toolbar">
        <select
          aria-label="Acción"
          value={accion}
          onChange={(e) => {
            setAccion(e.target.value)
            setDesde(0)
          }}
        >
          <option value="">Todas las acciones</option>
          {Object.entries(ACCION_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <span className="faint" style={{ fontSize: 12 }}>
          Cada lectura de datos de una persona y cada acción del panel queda registrada.
        </span>
      </div>
      {a.error && <ErrorBox error={a.error} onRetry={() => void a.reload()} />}
      {!a.data && !a.error && <Skeleton h={220} />}
      {a.data && a.data.items.length === 0 && (
        <div className="empty">
          <h2>Sin registros</h2>
        </div>
      )}
      {a.data && a.data.items.length > 0 && (
        <div className="card tight" style={{ overflowX: 'auto' }}>
          <table className="simple">
            <caption className="sr">Bitácora de auditoría</caption>
            <thead>
              <tr>
                <th scope="col">Cuándo</th>
                <th scope="col">Quién</th>
                <th scope="col">Qué</th>
                <th scope="col">Sobre</th>
                <th scope="col">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {a.data.items.map((e) => (
                <tr key={e.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{fechaHora(e.creadoEn)}</td>
                  <td>
                    {e.operadorId ? (
                      <Link className="link" to={`/admin/usuarios/${e.operadorId}`}>
                        {e.operadorEmail ?? e.operadorId.slice(0, 8)}
                      </Link>
                    ) : (
                      <span className="faint">Sistema</span>
                    )}
                  </td>
                  <td>{accionLabel(e.accion)}</td>
                  <td>
                    {e.objeto && UUID.test(e.objeto) && SOBRE_PERSONA.has(e.accion) ? (
                      <Link className="link mono" to={`/admin/usuarios/${e.objeto}`}>
                        {e.objeto.slice(0, 8)}
                      </Link>
                    ) : e.objeto ? (
                      <span className="mono faint">{e.objeto.slice(0, 24)}</span>
                    ) : e.orgId ? (
                      <span className="mono faint">org {e.orgId.slice(0, 8)}</span>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="faint" style={{ fontSize: 12 }}>
                    {resumenDetalle(e.detalle)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {a.data && (
        <Paginador
          desde={desde}
          cantidad={a.data.items.length}
          total={a.data.total}
          limite={LIMITE}
          onCambiar={setDesde}
        />
      )}
    </div>
  )
}
